/**
 * Users Tab - Active users list with analytics
 */

import { useEffect, useState } from 'react';
import { IconEye, IconRefresh } from '@tabler/icons-react';
import { Stack, Group, Button, Badge, ActionIcon, Select, Text } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { IdleTimeDisplay } from '../../../../components/ui/IdleTimeDisplay';
import { usePresenceData } from '../hooks/usePresenceData';
import type { UserActivitySummary, TimeRange } from '../types';

interface UsersTabProps {
  onViewUser?: (userId: string) => void;
}

export function UsersTab({ onViewUser }: UsersTabProps) {
  const { mostActiveUsers, fetchMostActiveUsers, loading } = usePresenceData();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  useEffect(() => {
    fetchMostActiveUsers(timeRange);
  }, [timeRange, fetchMostActiveUsers]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h`;
    }

    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const columns: DataTableColumn<UserActivitySummary & { id: string }>[] = [
    {
      key: 'userId',
      label: 'User ID',
      sortable: true,
    },
    {
      key: 'totalOnlineSeconds',
      label: 'Total Online',
      sortable: true,
      render: (_value, row) => formatDuration(row.totalOnlineSeconds),
    },
    {
      key: 'totalStatusChanges',
      label: 'Status Changes',
      sortable: true,
      render: (_value, row) => (
        <Badge variant="light">{row.totalStatusChanges}</Badge>
      ),
    },
    {
      key: 'mostCommonStatus',
      label: 'Common Status',
      render: (_value, row) => (
        <Badge color="blue">{row.mostCommonStatus}</Badge>
      ),
    },
    {
      key: 'platformsUsed',
      label: 'Platforms',
      render: (_value, row) => (
        <Group gap="xs">
          {row.platformsUsed?.map((platform) => (
            <Badge key={platform} size="xs" variant="outline">
              {platform}
            </Badge>
          ))}
        </Group>
      ),
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      sortable: true,
      render: (_value, row) => <IdleTimeDisplay lastSeen={row.lastSeen} size="xs" />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, row) => (
        <ActionIcon variant="light" onClick={() => onViewUser?.(row.userId)}>
          <IconEye size={16} />
        </ActionIcon>
      ),
    },
  ];

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Most active users in selected time range
        </Text>
        <Group>
          <Select
            value={timeRange}
            onChange={(value) => setTimeRange(value as TimeRange)}
            data={[
              { value: '7d', label: 'Last 7 days' },
              { value: '30d', label: 'Last 30 days' },
              { value: '90d', label: 'Last 90 days' },
            ]}
            style={{ width: 150 }}
          />
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} />}
            onClick={() => fetchMostActiveUsers(timeRange)}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={mostActiveUsers.map((u) => ({ ...u, id: u.userId }))}
        loading={loading}
        enableInternalSearch={true}
        searchPlaceholder="Search by user ID..."
        emptyMessage="No active users found"
      />
    </Stack>
  );
}
